<template>
  <div class="fts-state-design-inline">
    <div class="state-inline-group" v-if="editable && editMode">
      <fts-select
        v-model="model"
        :options="list"
        no-match-options-text="No matching state"
        no-options-text="No state found"
        :placeholder="placeholder"
        comfort-design
        class="state-inline-control-select"
        @option:selected="optionSelected"     
      >
        <!-- selected option container -->
        <fts-state slot="selected-option-container" slot-scope="{option}" :state="option" />
        <!-- option -->
        <fts-state slot="option" slot-scope="{option}" :state="option" />
        <!-- list header -->
        <div slot="list-header" class="fts-select__list-header">
          <fts-btn
            text
            color="primary"
            text-transform-init
            full-width
            @click="createState"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            Add New
          </fts-btn>
        </div>
        <!-- no options footer -->
        <div slot="no-options-footer" class="fts-select__no-options-footer">
          <fts-btn
            text
            color="primary"
            text-transform-init
            full-width
            @click="createState"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            Add New
          </fts-btn>
        </div>
      </fts-select>
      <fts-tippy content="discard change" v-if="model">
        <font-awesome-icon
          slot="trigger"
          :icon="['fas', 'redo-alt']"
          class="state-inline-group-action"
          @click="editMode = false"
        />
      </fts-tippy>
    </div>
    <!-- display -->
    <div
      class="state-inline-display"
      :class="{'state-inline-display--navi': navigatable}"
      v-else
    >
      <!-- state -->
      <div
        class="state-inline-display__state"
        @mouseenter="editVisible = true"
        @mouseleave="editVisible = false"
      >
        <!-- navigatable -->
        <fts-tippy
          content="go to state"
          class="state-inline-display-tippy"
          v-if="navigatable"
        >
          <fts-state
            slot="trigger"
            :state="state"
            :state-id="getStateId"
            :states="list"
            @click.native="navigate"
          />
        </fts-tippy>
        <!-- default -->
        <fts-state
          :state="state"
          :state-id="getStateId"
          :states="list"
          v-else
        />
        <!-- action -->
        <transition name="action-fade">
          <fts-tippy
            content="change state"
            class="state-inline-display__state-action"
            v-if="editable && editVisible"
          >
            <fts-btn
              slot="trigger"
              sm
              icon
              text
              color="primary"
              class="state-inline-display__state-action-btn"
              @click="enableEditMode"
            >
              <font-awesome-icon :icon="['far', 'edit']" />
            </fts-btn>
          </fts-tippy>
        </transition>
      </div>
      <!-- action:activity -->
      <div
        class="state-inline-display__action"
        @mouseenter="activityVisible = true"
        @mouseleave="activityVisible = false"
        @click="navigateNewActivityDesign"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        <transition name="action-fade">
          <div class="state-inline-display__action-overlay" v-if="activityVisible">
            <fts-btn
              outline
              color="primary"
              text-transform-init
              class="state-inline-display__action-btn"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="state-inline-display__action-btn-icon" /> New Activity
            </fts-btn>
          </div>
        </transition>
      </div>
    </div>
    <!-- state design modal -->
    <fts-state-design-modal ref="ftsStateDesignModal" />
  </div>
</template>

<script>
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import canvasDesign from "../commons/canvasDesign";

import FTSState from "../elements/FTSState";
import FTSSelect from "../elements/FTSSelect";
import FTSBtn from "../elements/FTSBtn";
import FTSTippy from "../elements/FTSTippy";

export default {
  components: {
    "fts-state": FTSState,
    "fts-select": FTSSelect,
    "fts-btn": FTSBtn,
    "fts-tippy": FTSTippy,
  },
  props: {
    value: [String, Number],
    editable: {
      type: Boolean,
      default: true,
    },
    navigatable: {
      type: Boolean,
      default: true,
    },
    activityNavigatable: {
      type: Boolean,
      default: true,
    },
    state: Object,
    stateId: [String, Number],
    list: Array,
    initialState: Boolean,
    initialEditMode: Boolean,
    placeholder: {
      type: String,
      default: "Create or select a State",
    },
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  data: () => ({
    editMode: false,
    editVisible: false,
    activityVisible: false,
  }),
  methods: {
    enableEditMode() {
      this.editMode = true;
      this.editVisible = false;
      this.activityVisible = false;
    },
    optionSelected(val) {
      this.editMode = false;
      this.$emit("state:selected", val.id);
    },
    navigate() {
      this.$emit("navigate", this.getStateId);
    },
    navigateNewActivityDesign() {
      this.$emit("navigate:activity", this.getStateId);
    },
    async createState() {
      const item = this.initialState ? { initial: true } : {};
      const result = await this.$refs.ftsStateDesignModal.show(
        { ...canvasDesign.initState(item) },
        null,
        null,
        null,
        { disableAdvanced: true }
      );

      if (result != null & result != true) {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        canvasDesign.setState(canvas, result);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        this.model = result.id;
        this.editMode = false;
        this.$emit("state:created", result.id);
      }
    },
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("updateValue", val);
      }
    },
    getStateId() {
      if (this.editable)
        return this.model;
      else if (this.state && this.state.id)
        return this.state.id;
      else if (this.stateId)
        return this.stateId;
    },
  },
  beforeCreate() {
		this.$options.components.FtsStateDesignModal = require('./FTSStateDesignModal').default
	},
  beforeMount() {
    if (this.initialEditMode)
      this.enableEditMode();
  },
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-state-design-inline {
  min-width: 0;
  .state-inline-group {
    display: inline-flex;
    align-items: center;
    .state-inline-control-select {
      min-width: 220px;
    }
    .state-inline-group-action {
      margin-left: .5rem;
      margin-right: .5rem;
      font-size: .875rem;
      color: var(--fts-gray-400);
      cursor: pointer;
    }
  }
  .state-inline-display {
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    .state-inline-display__state {
      position: relative;
      min-width: 0;
      .state-inline-display__state-action {
        @include absolute-center-y;
        right: .125rem;
        .state-inline-display__state-action-btn {
          box-shadow: var(--fts-elevation-2);
          background-color: #fff;
        }
      }
    }
    .state-inline-display__action {
      position: relative;
      display: flex;
      align-items: center;
      margin-left: .75rem;
      font-size: .875rem;
      color: var(--fts-primary);
      cursor: default;
      z-index: 1;
      .state-inline-display__action-overlay {
        position: absolute;
        left: calc(-0.5rem - 1px);
        white-space: nowrap;
        .state-inline-display__action-btn {
          min-height: auto;
          height: auto;
          padding: .375rem .5rem;
          border-radius: 10em;
          box-shadow: var(--fts-elevation-2);
          background-color: #fff;
          font-size: .875rem;
        }
        .state-inline-display__action-btn-icon {
          margin-right: .375rem;
        }
      }
    }
  }
  /* transition */
  .action-fade-enter-active, 
  .action-fade-leave-active {
    transition: opacity .2s linear;
  }
  .action-fade-enter, 
  .action-fade-leave-to {
    opacity: 0;
  }
}
</style>

<style lang="scss">
.fts-state-design-inline {
  .state-inline-display {
    &.state-inline-display--navi {
      .fts-state {
        transition: transform .2s linear;
        cursor: pointer;
        &:hover {
          transform: scale(1.075);
        }
      }
    }
    .state-inline-display-tippy {
      min-width: 0;
    }
    .fts-state {
      min-width: 3.5rem;
      > div {
        flex: 1;
        font-size: 1rem;
        text-align: center;
      }
    }
  }
}
</style>